import { debugLogger } from './debugLogger';
import { createEpisodeAnchor, formatPageStart, formatTocLink } from './narou/format';
import { parseDetailedMetaFromDoc } from './narou/metaParser';
import { NarouNovelInfoSchema } from './narou/schemas';
import { extractMaxPage, parseTocFromDoc } from './narou/tocParser';
import type {
  NarouDetailedMeta,
  NarouNovelInfo,
  NarouTocItem,
  NarouTocItemOrChapter,
} from './narou/types';
import {
  createEpisodeUrl,
  createInfoUrl,
  createTocPageUrl,
  createTocUrl,
  getProxyUrl,
} from './narou/url';

export type { NarouNovelInfo, NarouTocItem, NarouTocItemOrChapter } from './narou/types';

// biome-ignore lint/complexity/noStaticOnlyClass: Keep utility class namespace for wide compatibility
export class NarouImporter {
  // Cloudflare Pages のプロキシを経由するURLを生成
  private static getProxyUrl(targetUrl: string): string {
    return getProxyUrl(targetUrl);
  }

  public static createEpisodeAnchor(episodeNumber: number): string {
    return createEpisodeAnchor(episodeNumber);
  }

  public static formatTocLink(anchorId: string, label: string): string {
    return formatTocLink(anchorId, label);
  }

  public static formatPageStart(anchorId: string, pageTitle: string): string {
    return formatPageStart(anchorId, pageTitle);
  }

  /**
   * Nコードから小説のメタ情報と目次（エピソードリスト）を取得する
   */
  private static async fetchDetailedMeta(
    ncode: string,
    signal?: AbortSignal,
  ): Promise<NarouDetailedMeta> {
    const targetUrl = createInfoUrl(ncode);
    try {
      const res = await fetch(NarouImporter.getProxyUrl(targetUrl), { signal });
      if (!res.ok) return {};

      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      return parseDetailedMetaFromDoc(doc);
    } catch (e) {
      console.warn('Failed to fetch detailed meta:', e);
      return {};
    }
  }

  /**
   * Nコードから小説のメタ情報と目次（エピソードリスト）を取得する
   */
  public static async fetchToc(ncode: string, signal?: AbortSignal): Promise<NarouNovelInfo> {
    const targetUrl = createTocUrl(ncode);
    const res = await fetch(NarouImporter.getProxyUrl(targetUrl), { signal });

    if (!res.ok) {
      throw new Error(`Failed to fetch TOC: ${res.status}`);
    }

    const html = await res.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const title =
      doc.querySelector('.p-novel__title')?.textContent?.trim() ||
      doc.querySelector('.novel_title')?.textContent?.trim() ||
      '無題';

    const authorRaw =
      doc.querySelector('.p-novel__author')?.textContent ||
      doc.querySelector('.novel_writername')?.textContent ||
      '作者不明';
    const author = authorRaw.replace(/^作者\s*[:：]\s*/, '').trim() || '作者不明';

    const description =
      doc.querySelector('.p-novel__summary')?.textContent?.trim() ||
      doc.querySelector('#novel_ex')?.textContent?.trim() ||
      '';

    const tocItems: NarouTocItemOrChapter[] = [];
    const episodes: NarouTocItem[] = [];
    const seen = new Set<string>();

    parseTocFromDoc(doc, targetUrl, tocItems, episodes, seen);

    const maxPage = extractMaxPage(doc);
    if (maxPage > 1) {
      // To preserve rate limiting (500ms between requests) but parallelize network wait,
      // we stagger the initiation of each fetch by 500ms * index.
      // E.g. fetch p=2 starts at t=0, fetch p=3 starts at t=500, fetch p=4 at t=1000.
      // Then we wait for all of them to finish and parse them in order to avoid race conditions
      // modifying the shared tocItems and episodes arrays out of sequence.

      const promises: Promise<{ p: number; doc: Document | null }>[] = [];
      let globalAbort = false;

      for (let i = 0; i < maxPage - 1; i++) {
        const p = i + 2;
        const pageUrl = createTocPageUrl(ncode, p);

        const fetchPromise = (async () => {
          // Add (i + 1) * 500 delay to preserve the delay after the initial page fetch.
          await NarouImporter.sleep((i + 1) * 500, signal);
          if (globalAbort || signal?.aborted) return { p, doc: null };

          try {
            const pageRes = await fetch(NarouImporter.getProxyUrl(pageUrl), { signal });
            if (pageRes.ok) {
              const pageHtml = await pageRes.text();
              return { p, doc: parser.parseFromString(pageHtml, 'text/html') };
            }
          } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') {
              globalAbort = true;
              throw err;
            }
            console.warn(`Failed to fetch TOC page ${p}:`, err);
            globalAbort = true;
          }
          return { p, doc: null };
        })();

        promises.push(fetchPromise);
      }

      const results = await Promise.all(promises);

      // Sort the results strictly by page number and parse them in order
      results.sort((a, b) => a.p - b.p);

      for (const res of results) {
        // If a request failed or was aborted, stop parsing subsequent pages
        // to prevent gaps in the TOC if page 3 fails but page 4 succeeds.
        if (!res.doc) {
          break;
        }
        parseTocFromDoc(res.doc, targetUrl, tocItems, episodes, seen);
      }
    }

    episodes.sort((a, b) => a.episodeNumber - b.episodeNumber);

    // 作品情報から詳細メタデータを並行取得
    const detailedMeta = await NarouImporter.fetchDetailedMeta(ncode, signal);

    const novelInfo: NarouNovelInfo = {
      ncode,
      title,
      author,
      description,
      episodes,
      tocItems,
      ...detailedMeta,
    };

    // Zodバリデーションを実行し、問題がある場合はデバッグロガーに記録する
    const parsed = NarouNovelInfoSchema.safeParse(novelInfo);
    if (!parsed.success) {
      debugLogger.validation(ncode, parsed.error, novelInfo);
    } else {
      debugLogger.info(`TOC validation succeeded for N-Code: ${ncode}`);
    }

    return novelInfo;
  }

  private static normalizeRuby(root: Element, doc: Document): void {
    for (const ruby of root.querySelectorAll('ruby')) {
      const rbNode = ruby.querySelector('rb');
      let rbText = '';
      if (rbNode) {
        rbText = rbNode.textContent || '';
      } else {
        const child = ruby.childNodes[0];
        if (child && child.nodeType === Node.TEXT_NODE) {
          rbText = child.textContent || '';
        }
      }

      const rtText = ruby.querySelector('rt')?.textContent || '';

      if (rbText && rtText) {
        ruby.replaceWith(doc.createTextNode(`|${rbText}《${rtText}》`));
      }
    }
  }

  private static normalizeIllust(root: Element, doc: Document): void {
    for (const img of root.querySelectorAll('img')) {
      if (
        img.classList.contains('js-adjust-layout-illustflag') ||
        img.getAttribute('alt')?.includes('切替')
      ) {
        img.remove();
        continue;
      }

      let src = img.getAttribute('src') || '';
      if (src) {
        src = src.trim();
        if (src.startsWith('//')) {
          src = `https:${src}`;
        } else if (src.startsWith('/')) {
          src = `https://ncode.syosetu.com${src}`;
        }
        img.replaceWith(doc.createTextNode(`\n\n![挿絵](${src})\n\n`));
      }
    }
  }

  private static extractAndCleanText(el: Element): string {
    const processNode = (node: Node): string => {
      if (node.nodeType === 3) {
        // Node.TEXT_NODE
        return node.textContent || '';
      }
      if (node.nodeType === 1) {
        // Node.ELEMENT_NODE
        const element = node as Element;
        const tagName = element.tagName.toUpperCase();

        let childrenText = '';
        for (let i = 0; i < element.childNodes.length; i++) {
          childrenText += processNode(element.childNodes[i]);
        }

        if (tagName === 'BR') {
          return '\n';
        }
        if (tagName === 'B' || tagName === 'STRONG') {
          return `**${childrenText}**`;
        }
        if (tagName === 'S' || tagName === 'DEL') {
          return `~~${childrenText}~~`;
        }
        if (tagName === 'I' || tagName === 'EM') {
          if (element.classList.contains('emphasisDots')) {
            return `《《${childrenText}》》`;
          }
          return `*${childrenText}*`;
        }
        if (tagName === 'SPAN') {
          if (element.classList.contains('emphasisDots')) {
            return `《《${childrenText}》》`;
          }
        }

        return childrenText;
      }
      return '';
    };

    let text = '';
    for (let i = 0; i < el.childNodes.length; i++) {
      text += processNode(el.childNodes[i]);
    }

    return text.replace(/^[ \t]+|[ \t]+$/gm, '');
  }

  private static linesFromElement(element: Element, doc: Document): string[] {
    NarouImporter.normalizeRuby(element, doc);
    NarouImporter.normalizeIllust(element, doc);

    const ps = element.querySelectorAll('p');
    const lines: string[] = [];

    if (ps.length > 0) {
      for (const p of ps) {
        lines.push(NarouImporter.extractAndCleanText(p));
      }
    } else {
      const cleaned = NarouImporter.extractAndCleanText(element);
      if (cleaned) {
        lines.push(cleaned);
      }
    }

    return lines;
  }

  private static collectTexts(doc: Document, selector: string): string[] {
    return Array.from(doc.querySelectorAll(selector))
      .map((element) => NarouImporter.linesFromElement(element, doc).join('\n').trim())
      .filter(Boolean);
  }

  private static buildEpisodeSections(
    prefaceTexts: string[],
    bodyTexts: string[],
    afterwordTexts: string[],
  ): string {
    const sections: string[] = [];
    const prefaceText = prefaceTexts.join('\n').trim();
    const bodyText = bodyTexts.join('\n').trim();
    const afterwordText = afterwordTexts.join('\n').trim();

    if (prefaceText) {
      sections.push(`## 前書き\n\n${prefaceText}`);
    }

    if (bodyText) {
      sections.push(`<!-- ===== -->\n\n## 本文\n\n${bodyText}`);
    }

    if (afterwordText) {
      sections.push(`<!-- ===== -->\n\n## あとがき\n\n${afterwordText}`);
    }

    return sections.join('\n\n').trim();
  }

  private static buildHeuristicSectionsFromTexts(texts: string[]): string | null {
    const normalized = texts
      .flatMap((text) => text.split('\n'))
      .map((paragraph) => paragraph.trim());

    const prefaceIndex = normalized.findIndex((paragraph) =>
      /連載開始|よろしくお願いします|前書き|序章|導入/i.test(paragraph),
    );
    const afterwordIndex = normalized.findIndex((paragraph) =>
      /最後までお読み|ありがとうございました|あとがき|追記|おしまい/i.test(paragraph),
    );

    if (prefaceIndex < 0 && afterwordIndex < 0) {
      return null;
    }

    const prefaceTexts =
      prefaceIndex >= 0 ? [normalized.slice(0, prefaceIndex + 1).join('\n')] : [];
    const bodyStart = prefaceIndex >= 0 ? prefaceIndex + 1 : 0;
    const bodyEnd = afterwordIndex >= 0 ? afterwordIndex : normalized.length;
    const bodyTexts = [normalized.slice(bodyStart, bodyEnd).join('\n')].filter((text) =>
      text.trim(),
    );
    const afterwordTexts = afterwordIndex >= 0 ? [normalized.slice(afterwordIndex).join('\n')] : [];

    return NarouImporter.buildEpisodeSections(prefaceTexts, bodyTexts, afterwordTexts);
  }

  /**
   * 指定したエピソード番号の本文を取得し、なろうルビ記法でプレーンテキスト化する
   */
  public static async fetchEpisode(
    ncode: string,
    ep: number,
    signal?: AbortSignal,
  ): Promise<{ title: string; content: string }> {
    const targetUrl = createEpisodeUrl(ncode, ep);
    const res = await fetch(NarouImporter.getProxyUrl(targetUrl), { signal });

    if (!res.ok) {
      throw new Error(`Failed to fetch episode ${ep}: ${res.status}`);
    }

    const html = await res.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const subtitle =
      doc.querySelector('.novel_subtitle')?.textContent?.trim() ||
      doc.querySelector('.p-novel__title')?.textContent?.trim() ||
      `第${ep}部分`;

    const prefaceTexts = NarouImporter.collectTexts(doc, '.p-novel__text--preface, #novel_p');
    const bodyTexts = NarouImporter.collectTexts(
      doc,
      '.p-novel__text:not(.p-novel__text--preface):not(.p-novel__text--afterword), #novel_honbun',
    );
    const afterwordTexts = NarouImporter.collectTexts(doc, '.p-novel__text--afterword, #novel_a');

    if (prefaceTexts.length > 0 || bodyTexts.length > 0 || afterwordTexts.length > 0) {
      if (prefaceTexts.length === 0 && afterwordTexts.length === 0) {
        const heuristicContent = NarouImporter.buildHeuristicSectionsFromTexts(bodyTexts);
        if (heuristicContent) {
          return { title: subtitle, content: heuristicContent };
        }
      }

      const structuredContent = NarouImporter.buildEpisodeSections(
        prefaceTexts,
        bodyTexts,
        afterwordTexts,
      );
      return { title: subtitle, content: structuredContent || '本文が見つかりませんでした。' };
    }

    const jsTextTexts = NarouImporter.collectTexts(doc, '.js-novel-text');
    if (jsTextTexts.length === 0) {
      return { title: subtitle, content: '本文が見つかりませんでした。' };
    }

    return {
      title: subtitle,
      content:
        NarouImporter.buildEpisodeSections([], jsTextTexts, []) || jsTextTexts.join('\n\n').trim(),
    };
  }

  /**
   * ウェイトを挟むユーティリティ
   */
  public static sleep(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }

      const timer = setTimeout(() => {
        if (signal) signal.removeEventListener('abort', onAbort);
        resolve();
      }, ms);

      function onAbort() {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      }

      if (signal) {
        signal.addEventListener('abort', onAbort);
      }
    });
  }
}
